import { Button } from "@blurb/codex-react";
import { createStyles, Divider, Group, SelectItem, Stack } from "@mantine/core";
import { IconV3, RpiAlert } from "@rpi/openapi-core";
import { useSearchParams } from "react-router-dom";
import z from "zod";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";
import { awsRum } from "../../rum/cloudwatchRum";
import {
  filterByMatchingAttributes,
  findAvailableAttributeValues,
  ProductAttributesSchema,
  StandardProduct,
  useStandardProducts,
} from "../../hooks/useStandardProducts";
import { ChoiceStep } from "./ChoiceStep";
import { defaultGetIconCardConfig, IconCard } from "./IconCard";
import { produce } from "immer";
import { defaultGetTextCardConfig, TextCard } from "./TextCard";
import { NameAndSkuStep } from "./CreateNameSkuStep";
import { useProductCreateContext } from "./CustomerProductCreateView";
import { defaultProductImageConfig, ProductImage } from "./ProductImage";
import { ATTRIBUTES, isHardcover, isSoftcover } from "./attributeHelpers";
import { getDisplayName, getHardcoverProductImage, getSoftcoverProductImage } from "./attributeValueMappings";
import { PaperTypeStep } from "./ProductCreateSteps";

const productStateKey = "newProductState";

const NewProductStateSchema = z.strictObject({
  currentStepIndex: z.number(),
  attributes: ProductAttributesSchema,
  completedStepIndex: z.number(),
  nextFailed: z.boolean(),
  customerProductName: z.string().max(128),
  customerProductSku: z.string().max(64).optional(),
  saveError: z.string(),
});
export type NewProductState = z.infer<typeof NewProductStateSchema>;
type SetStateCallback = (recipe: (draft: NewProductState) => void) => void;

export type StepProps = {
  state: NewProductState;
  setState: SetStateCallback;
  attributeKey: string | undefined;
  standardProducts: StandardProduct[];
};

type StepConfig = {
  stepKey: string;
  label: string;
  attributeKey?: string;
  render: (props: StepProps) => JSX.Element;
};

const stepDefinitions: StepConfig[] = [
  {
    stepKey: "cover-type" as const,
    label: "Cover Type",
    attributeKey: ATTRIBUTES.COVER_TYPE,
    render: (props) => (
      <ChoiceStep
        {...props}
        renderCard={(value: string) => {
          const config = defaultGetTextCardConfig(ATTRIBUTES.COVER_TYPE)(value);
          return config ? <TextCard key={value} {...config} /> : null;
        }}
        renderImage={(value: string) => {
          const config = defaultProductImageConfig(ATTRIBUTES.COVER_TYPE)(value);
          return config ? <ProductImage {...config} /> : null;
        }}
        flexDirection="column"
      />
    ),
  },
  {
    stepKey: "cover-finish" as const,
    label: "Cover Finish",
    attributeKey: ATTRIBUTES.COVER_FINISH,
    render: (props) => (
      <ChoiceStep
        {...props}
        renderCard={(value: string) => {
          const config = defaultGetTextCardConfig(ATTRIBUTES.COVER_FINISH)(value);
          return config ? <TextCard key={value} {...config} /> : null;
        }}
        renderImage={(value: string) => {
          const displayName = getDisplayName(ATTRIBUTES.COVER_FINISH, value);

          const specializedImage = isSoftcover(props.state.attributes)
            ? getSoftcoverProductImage(ATTRIBUTES.COVER_FINISH, value)
            : getHardcoverProductImage(ATTRIBUTES.COVER_FINISH, value);

          return displayName && specializedImage ? (
            <ProductImage displayName={displayName} image={specializedImage} />
          ) : null;
        }}
        flexDirection="column"
      />
    ),
  },
  {
    stepKey: "orientation" as const,
    label: "Orientation",
    attributeKey: ATTRIBUTES.ORIENTATION,
    render: (props) => (
      <ChoiceStep
        {...props}
        renderCard={(value: string) => {
          const config = defaultGetIconCardConfig(ATTRIBUTES.ORIENTATION)(value);
          return config ? <IconCard key={value} {...config} /> : null;
        }}
      />
    ),
  },
  {
    stepKey: "size" as const,
    label: "Size (width x height)",
    attributeKey: ATTRIBUTES.DIMENSIONS,
    render: (props) => {
      const filteredProducts = filterByMatchingAttributes(props.standardProducts, attributesForFiltering(props.state));
      const availableDimensions = findAvailableAttributeValues(filteredProducts, ATTRIBUTES.DIMENSIONS).map(String);
      const currentOrientation = props.state.attributes.orientation;

      return (
        <ChoiceStep
          {...props}
          instructionText={
            isHardcover(props.state.attributes) && (
              <>
                Please select a size for your book block (internal pages) to continue.
                <br />
                <br />
                Note: the hardcover binding will add 0.25" to the final dimensions.
              </>
            )
          }
          getFilterAttributes={(state) => {
            const attributesWithoutOrientation = { ...attributesForFiltering(state), orientation: undefined };
            return attributesWithoutOrientation;
          }}
          sortValues={(values) => {
            return values.sort((a, b) => {
              const aDisabled = !availableDimensions.includes(String(a));
              const bDisabled = !availableDimensions.includes(String(b));
              if (aDisabled === bDisabled) return 0;
              return aDisabled ? 1 : -1;
            });
          }}
          renderCard={(value: string) => {
            const isDisabled = !availableDimensions.includes(value);
            const config = defaultGetIconCardConfig(ATTRIBUTES.DIMENSIONS)(value);

            const renderTooltip = isDisabled
              ? () => (
                  <>
                    This size is not available with the
                    <br />
                    <strong>{currentOrientation}</strong> orientation
                  </>
                )
              : undefined;

            return config ? (
              <IconCard key={value} {...config} disabled={isDisabled} renderTooltip={renderTooltip} />
            ) : null;
          }}
        />
      );
    },
  },
  {
    stepKey: "bind" as const,
    label: "Bind",
    attributeKey: ATTRIBUTES.BIND_TYPE,
    render: (props) => (
      <ChoiceStep
        {...props}
        renderCard={(value: string) => {
          const config = defaultGetTextCardConfig(ATTRIBUTES.BIND_TYPE)(value);
          return config ? <TextCard key={value} {...config} /> : null;
        }}
        renderImage={(value: string) => {
          const displayName = getDisplayName(ATTRIBUTES.BIND_TYPE, value);

          const specializedImage = isSoftcover(props.state.attributes)
            ? getSoftcoverProductImage(ATTRIBUTES.BIND_TYPE, value)
            : getHardcoverProductImage(ATTRIBUTES.BIND_TYPE, value);

          return displayName && specializedImage ? (
            <ProductImage displayName={displayName} image={specializedImage} />
          ) : null;
        }}
        flexDirection="column"
      />
    ),
  },
  {
    stepKey: "paper-type" as const,
    label: "Paper Type",
    attributeKey: ATTRIBUTES.TEXT_STOCK,
    render: PaperTypeStep,
  },
  {
    stepKey: "sku" as const,
    label: "Name & SKU",
    render: (props) => <NameAndSkuStep {...props} />,
  },
];

const initialState: NewProductState = Object.freeze({
  currentStepIndex: 0,
  completedStepIndex: -1,
  attributes: {},
  nextFailed: false,
  customerProductName: "",
  customerProductSku: undefined,
  saveError: "",
});

function getStateFromSearch(search: URLSearchParams): NewProductState {
  const stateFromParams = search.get(productStateKey);
  if (stateFromParams === null) {
    return initialState;
  }
  try {
    const jsonState = JSON.parse(stateFromParams);
    return NewProductStateSchema.parse(jsonState);
  } catch (error) {
    // return the default if there are any issues with parsing the URL state
    awsRum?.recordError(error);
    return initialState;
  }
}

export function ProductCreateNavigator() {
  const [search, setSearch] = useSearchParams();
  const { standardProducts, isLoading, isError } = useStandardProducts();
  const { loading } = useProductCreateContext();

  const state = getStateFromSearch(search);
  const currentDefinition = stepDefinitions[state.currentStepIndex];
  const CurrentStep = currentDefinition.render;

  const setState = (recipe: (draft: NewProductState) => void) => {
    setSearch((currentSearch) => {
      const currentState = getStateFromSearch(currentSearch);
      const nextState = produce(currentState, recipe);
      const nextSearch = new URLSearchParams(currentSearch);
      nextSearch.set(productStateKey, JSON.stringify(nextState));
      return nextSearch;
    });
  };

  function handleNavigate(newStep: string) {
    const newStepIndex = stepDefinitions.findIndex((step) => step.stepKey === newStep);
    setState((draft) => {
      draft.currentStepIndex = newStepIndex;
    });
  }

  const attributeKey = currentDefinition.attributeKey;

  if (isLoading) {
    return <RpiAlert>Loading products...</RpiAlert>;
  }

  if (isError || !standardProducts) {
    return <RpiAlert>Failed to load products. Please refresh the page.</RpiAlert>;
  }

  const dropDownOptions = getStepSelectItems(state);

  return (
    <Stack>
      <RpaDropdown
        aria-label={
          dropDownOptions.find((option) => option.value === currentDefinition.stepKey)?.label ?? "Step navigation"
        }
        role="combobox"
        disabled={loading}
        data={dropDownOptions}
        value={currentDefinition.stepKey}
        onChange={(value) => handleNavigate(value ?? currentDefinition.stepKey)}
      />
      <Divider />
      <CurrentStep state={state} setState={setState} attributeKey={attributeKey} standardProducts={standardProducts} />
    </Stack>
  );
}

function getStepSelectItems(state: NewProductState): SelectItem[] {
  return stepDefinitions.map<SelectItem>((step, index) => {
    return {
      value: step.stepKey,
      label: `Step ${index + 1}: ${step.label}`,
      disabled: state.completedStepIndex + 1 < index,
    };
  });
}

export function invalidateSubsequentSteps(draft: NewProductState, fromIndex: number) {
  draft.attributes = attributesWithSubsequentStepsRemoved(draft, fromIndex);
  draft.customerProductSku = undefined;
  draft.saveError = "";
}

function attributesWithSubsequentStepsRemoved(state: NewProductState, fromIndex: number) {
  return stepDefinitions.reduce((acc, stepDef, index) => {
    if (index > fromIndex && stepDef.attributeKey !== undefined) {
      return { ...acc, [stepDef.attributeKey]: undefined };
    }
    return acc;
  }, state.attributes);
}

export function attributesForFiltering(state: NewProductState) {
  return attributesWithSubsequentStepsRemoved(state, state.currentStepIndex - 1);
}

const useProductNavButtonStyles = createStyles({
  growButton: {
    "flex-grow": "1 !important",
    flexBasis: 0,
  },
  buttonText: {
    flexGrow: 1,
    textAlign: "center",
  },
  soloNextText: {
    flexGrow: 1,
    textAlign: "center",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  soloNextIcon: {
    marginLeft: "auto",
  },
  container: {
    gap: "var(--codex-spacing-2)",
  },
});

type ProductCreateNavButtonsProps = {
  state: NewProductState;
  setState: SetStateCallback;
  onNext?: () => void;
  onDone?: () => void;
  loading?: boolean;
};

export function ProductCreateNavButtons({ state, setState, onNext, onDone, loading }: ProductCreateNavButtonsProps) {
  const { classes } = useProductNavButtonStyles();
  const { onClose } = useProductCreateContext();
  const isFirst = state.currentStepIndex === 0;
  const hasNext = state.currentStepIndex < stepDefinitions.length - 1;

  const onBack = () => {
    setState((draft) => {
      draft.currentStepIndex -= 1;
    });
  };

  return (
    <Stack className={classes.container}>
      <Group sx={{ marginTop: "var(--codex-spacing-6)" }}>
        {!isFirst && (
          <Button
            variant="outlined"
            intent="primary"
            className={classes.growButton}
            onClick={onBack}
            disabled={loading}
          >
            <IconV3 icon="chevron_backward" color="inherit" />
            <span className={classes.buttonText}>Back</span>
          </Button>
        )}
        {hasNext && onNext && (
          <Button
            variant="filled"
            intent="primary"
            className={classes.growButton}
            onClick={onNext}
            data-testid="nextButton"
          >
            <span className={isFirst ? classes.soloNextText : classes.buttonText}>Next</span>
            <IconV3 className={isFirst ? classes.soloNextIcon : undefined} icon="chevron_forward" color="inherit" />
          </Button>
        )}
        {onDone && (
          <Button
            variant="filled"
            intent="primary"
            className={classes.growButton}
            data-testid="doneButton"
            onClick={onDone}
            loading={loading}
          >
            Done
          </Button>
        )}
      </Group>
      <Button variant="text" onClick={onClose} disabled={loading}>
        Cancel
      </Button>
    </Stack>
  );
}
